/**
 * SourceTree — the 3-level research source picker.
 *
 *   Jurisdiction  (Bulgaria · 6 indexes · 52K)
 *     Category    (Case Law · select all)
 *       Index     (Supreme Court of Cassation (VKS) · 40,297)   ← selectable
 *
 * Selecting an INDIVIDUAL index is the point: the backend now returns one row
 * per live index and honours `source_identifier` verbatim, so a user can
 * search only VKS, or SAC + VKS, instead of "all Bulgarian case law".
 *
 * Select-all lives at the category and jurisdiction levels; a partly-selected
 * group renders an indeterminate box rather than a misleading tick.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiCaretDownLight,
  PiCaretRightLight,
  PiCheckLight,
  PiDatabaseLight,
  PiMagnifyingGlassLight,
  PiMinusLight,
  PiXLight,
} from 'react-icons/pi';
import Flag from '../shared/TailwindComponents/Flag';
import useDebouncedValue from './useDebouncedValue';
import useJurisdictionOrder from './useJurisdictionOrder';
import { ResearchSource } from '../../store/actions/ChatAction';
import {
  JurisdictionGroup,
  SelectionState,
  buildSourceTree,
  filterSourceTree,
  formatDocumentCount,
  formatExactCount,
  groupSelectionState,
  isSourceSelectable,
  selectionSet,
  setSourcesSelected,
  sourceKey,
  toggleSource,
} from './sources';

/* ------------------------------------------------------------------ */
/*  Tri-state checkbox                                                 */
/* ------------------------------------------------------------------ */

const CheckBox: React.FC<{ state: SelectionState; disabled?: boolean }> = ({ state, disabled }) => (
  <span
    data-testid="checkbox"
    data-state={state}
    aria-hidden
    className={clsx(
      'w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center',
      'transition-colors duration-200 ease-slide',
      state === 'none'
        ? 'border-hairline'
        : 'bg-champagne border-champagne text-ink',
      disabled && 'opacity-50'
    )}
  >
    {state === 'all' && <PiCheckLight className="w-2.5 h-2.5" />}
    {state === 'partial' && <PiMinusLight className="w-2.5 h-2.5" />}
  </span>
);

/* ------------------------------------------------------------------ */
/*  Rows                                                               */
/* ------------------------------------------------------------------ */

const rowClass = (selected: boolean, disabled = false) =>
  clsx(
    'flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs text-left',
    'transition-all duration-200 ease-slide',
    disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
    selected
      ? 'bg-surface-sunken/80 border border-hairline-soft text-fg-strong shadow-sm'
      : 'border border-transparent text-fg-body hover:bg-surface-sunken/50'
  );

interface Props {
  /** Every available index, as returned by `available-sources`. */
  jurisdictionSources: ResearchSource[];
  userDatasources: ResearchSource[];
  selected: ResearchSource[];
  onChange: (next: ResearchSource[]) => void;
  /** Collapsed/expanded per jurisdiction code — owned by the host. */
  expanded: Record<string, boolean>;
  onToggleExpanded: (code: string) => void;
}

const SourceTree: React.FC<Props> = ({
  jurisdictionSources,
  userDatasources,
  selected,
  onChange,
  expanded,
  onToggleExpanded,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  // The input stays instant; only the filter waits for typing to settle.
  const settledQuery = useDebouncedValue(query, 150);

  const allJurisdictions = React.useMemo(
    () => buildSourceTree(jurisdictionSources),
    [jurisdictionSources]
  );
  /**
   * The filtered view IS the tree everything else works from — selection,
   * the whole-jurisdiction and whole-category toggles, the counts. So a
   * select-all while filtering selects exactly what is on screen.
   */
  const tree = React.useMemo(
    () => filterSourceTree(allJurisdictions, settledQuery),
    [allJurisdictions, settledQuery]
  );

  const visibleDatasources = React.useMemo(() => {
    const needle = settledQuery.trim().toLowerCase();
    if (!needle) return userDatasources;
    return userDatasources.filter((src) =>
      [src.source_name, src.source_identifier, src.description]
        .some((field) => String(field ?? '').toLowerCase().includes(needle))
    );
  }, [userDatasources, settledQuery]);

  const isFiltering = settledQuery.trim() !== '';
  const nothingMatches = isFiltering && tree.length === 0 && visibleDatasources.length === 0;

  const chosen = React.useMemo(() => selectionSet(selected), [selected]);

  /* ── Drag to reorder jurisdictions ────────────────────────────
   * `available-sources` already arrives in the user's stored order, so the
   * default render needs no sorting. `sort` only matters after a drag, when
   * the local sequence is ahead of the next fetch.
   */
  const jurisdictionOrder = useJurisdictionOrder();
  const visibleCodes = React.useMemo(() => tree.map((g) => g.code), [tree]);
  const ordered = React.useMemo(
    () => jurisdictionOrder.sort(visibleCodes),
    [jurisdictionOrder, visibleCodes]
  );
  const orderedTree = React.useMemo(
    () => ordered
      .map((code) => tree.find((g) => g.code === code))
      .filter((g): g is JurisdictionGroup => !!g),
    [ordered, tree]
  );

  const [dragging, setDragging] = React.useState<string | null>(null);
  const [dropIndex, setDropIndex] = React.useState<number | null>(null);
  const rowsRef = React.useRef(new Map<string, HTMLElement>());
  const pressRef = React.useRef<{ y: number; code: string; active: boolean } | null>(null);

  /**
   * Which slot is the pointer over?
   *
   * Measured against each group's own box rather than tracked from the drag
   * start, so a list that reflows mid-drag still lands where the user is
   * pointing.
   */
  const slotAt = React.useCallback((clientY: number): number => {
    const rows = ordered
      .map((code) => rowsRef.current.get(code))
      .filter((el): el is HTMLElement => !!el);
    for (let i = 0; i < rows.length; i += 1) {
      const box = rows[i].getBoundingClientRect();
      if (clientY < box.top + box.height / 2) return i;
    }
    return Math.max(rows.length - 1, 0);
  }, [ordered]);

  const endDrag = React.useCallback((clientY?: number) => {
    const press = pressRef.current;
    pressRef.current = null;
    setDragging(null);
    setDropIndex(null);
    if (!press?.active || typeof clientY !== 'number') return;
    jurisdictionOrder.reorder(ordered, press.code, slotAt(clientY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordered, slotAt, jurisdictionOrder.reorder]);

  // A drag must not survive the gesture: a release anywhere ends it, and
  // Escape cancels without reordering.
  React.useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => setDropIndex(slotAt(e.clientY));
    const onUp = (e: PointerEvent) => endDrag(e.clientY);
    const onCancel = () => endDrag();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
    };
  }, [dragging, slotAt, endDrag]);

  const DRAG_THRESHOLD = 4;

  const canReorder = jurisdictionOrder.canReorder;

  const onHeaderPointerDown = (code: string) => (e: React.PointerEvent) => {
    // Not storable (the endpoint is unavailable) — do not start a gesture
    // whose result would silently disappear.
    if (!canReorder) return;
    if (typeof e.button === 'number' && e.button > 0) return;
    pressRef.current = { y: e.clientY, code, active: false };
  };

  const onHeaderPointerMove = (e: React.PointerEvent) => {
    const press = pressRef.current;
    if (!press || press.active) return;
    // Only past a threshold, so a click on a toggle is never a drag.
    if (Math.abs(e.clientY - press.y) < DRAG_THRESHOLD) return;
    press.active = true;
    setDragging(press.code);
    setDropIndex(slotAt(e.clientY));
  };

  const onHeaderPointerUp = (e: React.PointerEvent) => endDrag(e.clientY);

  const renderJurisdiction = (group: JurisdictionGroup, groupIdx: number) => {
    // Default to expanded: a collapsed picker hides the whole point of it.
    const isOpen = expanded[group.code] !== false;
    const groupState = groupSelectionState(group.indexes, selected);

    return (
      <div
        key={group.code}
        ref={(el) => {
          if (el) rowsRef.current.set(group.code, el);
          else rowsRef.current.delete(group.code);
        }}
        data-testid="jurisdiction-group"
        data-code={group.code}
        data-dragging={dragging === group.code}
        data-drop-target={dropIndex === groupIdx}
        className={clsx(
          'reveal-item transition-all duration-200 ease-slide',
          dragging === group.code && 'opacity-60',
          dropIndex === groupIdx && dragging && dragging !== group.code &&
            'border-t-2 border-champagne'
        )}
        style={{ '--reveal-delay': `${Math.min(groupIdx, 8) * 35}ms` } as React.CSSProperties}
      >
        {/* ── Level 1: jurisdiction ─────────────────────────────
            The header row is the drag handle. The controls inside it stop
            propagation, so expanding or selecting is never a drag. */}
        <div
          data-testid="jurisdiction-header"
          data-code={group.code}
          data-reorderable={canReorder}
          onPointerDown={onHeaderPointerDown(group.code)}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          className={clsx(
            'flex items-center gap-1.5 px-1 py-1 rounded-md select-none',
            'hover:bg-surface-sunken/60 transition-colors duration-200 ease-slide',
            // The grab cursor is a promise; only make it when it can be kept.
            canReorder && 'cursor-grab active:cursor-grabbing touch-none'
          )}>
          <button
            type="button"
            onClick={() => onToggleExpanded(group.code)}
            aria-label={`${isOpen ? t('Collapse') : t('Expand')} ${group.name}`}
            className="text-fg-muted hover:text-fg-strong transition-colors duration-200 ease-slide"
          >
            {isOpen
              ? <PiCaretDownLight className="w-3 h-3" />
              : <PiCaretRightLight className="w-3 h-3" />}
          </button>

          <button
            type="button"
            data-testid="jurisdiction-select-all"
            data-state={groupState}
            onClick={() => onChange(setSourcesSelected(group.indexes, selected, groupState !== 'all'))}
            className="flex items-center gap-2 flex-1 min-w-0 text-left"
          >
            <CheckBox state={groupState} />
            <Flag countryCode={group.code} size="sm" />
            <span className="text-sm font-medium text-fg-body truncate">{group.name}</span>
            <span className="ml-auto flex items-center gap-1.5 flex-shrink-0">
              <span className="text-[10px] text-fg-muted tabular-nums">
                {group.indexes.length} {t(group.indexes.length === 1 ? 'source' : 'sources')}
              </span>
              {group.documentCount > 0 && (
                <span className="text-[10px] text-fg-muted tabular-nums">
                  · {formatDocumentCount(group.documentCount)}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* ── Level 2 + 3: categories and their indexes ─────── */}
        {isOpen && (
          <div className="ml-4 mt-0.5 space-y-1.5 border-l border-hairline-soft pl-2">
            {group.categories.map((category) => {
              const categoryState = groupSelectionState(category.indexes, selected);
              return (
                <div key={category.category} data-testid="category-group" data-category={category.category}>
                  <button
                    type="button"
                    data-testid="category-select-all"
                    data-state={categoryState}
                    onClick={() =>
                      onChange(setSourcesSelected(category.indexes, selected, categoryState !== 'all'))
                    }
                    title={t('Select all in category')}
                    className="flex items-center gap-2 w-full px-1 py-0.5 rounded-md text-left
                               hover:bg-surface-sunken/50 transition-colors duration-200 ease-slide"
                  >
                    <CheckBox state={categoryState} />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted truncate">
                      {category.label}
                    </span>
                    <span className="ml-auto text-[10px] text-fg-muted tabular-nums flex-shrink-0">
                      {category.indexes.length}
                    </span>
                  </button>

                  <div className="ml-5 space-y-0.5 mt-0.5">
                    {category.indexes.map((index) => {
                      const isSelected = chosen.has(sourceKey(index));
                      return (
                        <button
                          type="button"
                          key={sourceKey(index)}
                          data-testid="index-row"
                          data-index={index.source_identifier}
                          title={`${index.source_name || index.source_identifier} — ${index.source_identifier}`}
                          onClick={() => onChange(toggleSource(index, selected))}
                          className={rowClass(isSelected)}
                        >
                          <CheckBox state={isSelected ? 'all' : 'none'} />
                          <span className="truncate flex-1">
                            {index.source_name || index.source_identifier}
                          </span>
                          {/* Exact count — an index is a concrete corpus. */}
                          {index.document_count != null && (
                            <span className="text-[10px] text-fg-muted flex-shrink-0 tabular-nums">
                              {formatExactCount(index.document_count)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3 font-sans" data-testid="source-tree">
      {/* ── Filter ──────────────────────────────────────────── */}
      <div className="relative">
        <PiMagnifyingGlassLight
          className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-muted"
          aria-hidden
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
          placeholder={t('Filter sources')}
          aria-label={t('Filter sources')}
          data-testid="source-search"
          className="w-full pl-7 pr-7 py-1.5 rounded-lg text-xs bg-surface-sunken
                     text-fg-body border border-hairline-soft
                     placeholder:text-fg-muted focus:outline-none
                     focus:border-champagne/50 transition-colors duration-200 ease-slide"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            data-testid="source-search-clear"
            aria-label={t('Clear filter')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted
                       hover:text-fg-strong transition-colors duration-200 ease-slide"
          >
            <PiXLight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {nothingMatches && (
        <p className="text-xs text-fg-muted py-2 text-center" data-testid="source-no-matches">
          {t('No matches')}
        </p>
      )}

      {/* ── Jurisdictions ───────────────────────────────────── */}
      <div className="space-y-1">
        {orderedTree.length > 0 ? (
          orderedTree.map(renderJurisdiction)
        ) : !isFiltering ? (
          <p className="text-xs text-fg-muted py-2">{t('No jurisdictional sources available')}</p>
        ) : null}
      </div>

      {/* ── Your data sources ───────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted mb-1 flex items-center gap-1.5">
          <PiDatabaseLight className="w-3.5 h-3.5" aria-hidden />
          {t('Your Data Sources')}
        </p>
        {visibleDatasources.length > 0 ? (
          <div className="space-y-0.5">
            {visibleDatasources.map((src, i) => {
              const isSelected = chosen.has(sourceKey(src));
              const isReady = isSourceSelectable(src);
              return (
                <button
                  type="button"
                  key={sourceKey(src)}
                  data-testid="datasource-row"
                  data-status={src.status || 'unknown'}
                  title={src.description || src.source_name}
                  disabled={!isReady}
                  onClick={() => onChange(toggleSource(src, selected))}
                  style={{ '--reveal-delay': `${Math.min(i, 8) * 35}ms` } as React.CSSProperties}
                  className={clsx('reveal-item', rowClass(isSelected, !isReady))}
                >
                  <CheckBox state={isSelected ? 'all' : 'none'} disabled={!isReady} />
                  <PiDatabaseLight className="w-3.5 h-3.5 text-fg-muted flex-shrink-0" aria-hidden />
                  <span className="truncate flex-1">{src.source_name || src.source_identifier}</span>
                  {/* Still indexing: listed, but not selectable. */}
                  {!isReady ? (
                    <span className="px-1.5 py-0 rounded-full text-[10px] font-medium flex-shrink-0
                                     bg-surface-sunken border border-hairline-soft text-fg-muted">
                      {t(src.status || 'processing')}
                    </span>
                  ) : (
                    src.document_count != null && (
                      <span className="text-[10px] text-fg-muted flex-shrink-0 tabular-nums">
                        {formatExactCount(src.document_count)}
                      </span>
                    )
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-fg-muted py-1">{t('No data sources uploaded yet')}</p>
        )}
      </div>
    </div>
  );
};

export default SourceTree;
